/**
 * SystemCommonCodeCreatePage Helpers
 * 공통코드 추가/수정 페이지 헬퍼 함수 및 상수
 */

import { USE_YN } from '../../shared/config/system.config';

// ======== 타입 정의 ========

/** 공통코드 폼 아이템 타입 */
export type CommonCodeFormItem = {
    id: string;
    comCode: string;
    comCdName: string;
    comSeqno: string;
    seqNo?: number;
    useYn: string;
    val1: string;
    val2: string;
    val3: string;
    val4: string;
    val5: string;
    visibleValueFields: number;
};

/** 그룹 폼 데이터 타입 */
export type GroupFormData = {
    grpCode: string;
    grpName: string;
    grpVal: string;
    seqNo?: number;
};

// ======== 상수 ========

/** 토스트 메시지 */
export const TOAST_MESSAGES = {
    MIN_CODE_REQUIRED: '최소 1개의 공통코드는 필요합니다.',
    LOGIN_NOT_FOUND: '로그인 정보를 찾을 수 없습니다.',
    GROUP_SAVE_FAIL: '그룹코드 저장에 실패했습니다.',
    NO_CHANGES: '변경사항이 없습니다.',
    SAVE_SUCCESS: (isEdit: boolean) => isEdit ? '공통코드가 수정되었습니다.' : '공통코드가 추가되었습니다.',
    SAVE_FAIL: (isEdit: boolean) => isEdit ? '공통코드 수정에 실패했습니다.' : '공통코드 추가에 실패했습니다.',
} as const;

/** 유효성 검사 메시지 */
export const VALIDATION_MESSAGES = {
    GRP_CODE_REQUIRED: '그룹코드를 입력해주세요.',
    GRP_NAME_REQUIRED: '그룹명을 입력해주세요.',
    COM_CODE_REQUIRED: (index: number) => `공통코드 ${index}의 공통코드를 입력해주세요.`,
    COM_NAME_REQUIRED: (index: number) => `공통코드 ${index}의 공통코드명을 입력해주세요.`,
} as const;

/** 기본 코드값 필드 개수 */
export const DEFAULT_VALUE_FIELDS = 2;

/** 최대 코드값 필드 개수 */
export const MAX_VALUE_FIELDS = 5;

// ======== 헬퍼 함수 ========

/**
 * 새 공통코드 아이템 생성
 * @returns 초기화된 공통코드 아이템
 */
export const createEmptyCodeItem = (): CommonCodeFormItem => ({
    id: `code-${Date.now()}-${Math.random()}`,
    comCode: '',
    comCdName: '',
    comSeqno: '',
    useYn: USE_YN.YES,
    val1: '',
    val2: '',
    val3: '',
    val4: '',
    val5: '',
    visibleValueFields: DEFAULT_VALUE_FIELDS,
});

/**
 * 초기 그룹 폼 데이터 생성
 * @returns 초기화된 그룹 폼 데이터
 */
export const createEmptyGroupFormData = (): GroupFormData => ({
    grpCode: '',
    grpName: '',
    grpVal: '',
    seqNo: undefined,
});

/**
 * 코드값 필드 토글 시 새 상태 계산
 * @param code 현재 코드 아이템
 * @param fieldIndex 토글할 필드 인덱스
 */
export const getToggledValueFieldState = (
    code: CommonCodeFormItem,
    fieldIndex: number
): Partial<CommonCodeFormItem> => {
    if (fieldIndex < code.visibleValueFields) {
        // 필드 삭제
        return {
            visibleValueFields: fieldIndex,
            val1: fieldIndex >= 1 ? code.val1 : '',
            val2: fieldIndex >= 2 ? code.val2 : '',
            val3: fieldIndex >= 3 ? code.val3 : '',
            val4: fieldIndex >= 4 ? code.val4 : '',
            val5: fieldIndex >= 5 ? code.val5 : '',
        };
    }
    // 필드 추가
    return { visibleValueFields: Math.min(fieldIndex + 1, MAX_VALUE_FIELDS) };
};

/**
 * 폼 유효성 검사
 * @param formData 그룹 폼 데이터
 * @param commonCodes 공통코드 배열
 * @returns 에러 메시지 (없으면 null)
 */
export const validateForm = (
    formData: GroupFormData,
    commonCodes: CommonCodeFormItem[]
): string | null => {
    if (!formData.grpCode.trim()) {
        return VALIDATION_MESSAGES.GRP_CODE_REQUIRED;
    }
    if (!formData.grpName.trim()) {
        return VALIDATION_MESSAGES.GRP_NAME_REQUIRED;
    }

    for (let i = 0; i < commonCodes.length; i++) {
        const code = commonCodes[i];
        if (!code.comCode.trim()) {
            return VALIDATION_MESSAGES.COM_CODE_REQUIRED(i + 1);
        }
        if (!code.comCdName.trim()) {
            return VALIDATION_MESSAGES.COM_NAME_REQUIRED(i + 1);
        }
    }

    return null;
};
